import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { find, isEmpty, isNil } from 'lodash';
import { Col, Card, ListGroup, Form, Popover, OverlayTrigger } from 'react-bootstrap';

import * as layerOps from '../../../api/Layers/layer-operations';
import * as styleOps from '../../../api/Styles/style-operations';
import getDefaultStyle from '../../../modules/get-default-style';

import DoubleRangeSlider from '../DoubleRangeSlider';

const TransparencyPopover = ({
  changeTransparency,
  layer,
  layersTransparency,
  style,
  ...props
}) => {
  let transparency = 1;
  let title = '';
  const layerTransparency = find(layersTransparency, ['id', layerOps.getId(layer)]);

  if (styleOps.isRaster(style)) {
    transparency = styleOps.getRasterOpacityPercentage(style) / 100;
    title = 'Opacity';
  } else {
    transparency = styleOps.getAlpha(style);
    title = 'Alpha';
  }
  if (!isNil(layerTransparency)) transparency = layerTransparency.value;

  return (
    <Popover {...props} id="popover-basic">
      <Form.Group className="m-2">
        <Form.Label>{title}</Form.Label>
        <Form.Control
          type="range"
          defaultValue={transparency}
          min="0"
          max="1"
          step="0.01"
          onChange={event => changeTransparency(event, layer)}
        />
      </Form.Group>
    </Popover>
  );
};

TransparencyPopover.propTypes = {
  changeTransparency: PropTypes.func.isRequired,
  layer: PropTypes.object.isRequired,
  layersTransparency: PropTypes.array.isRequired,
  style: PropTypes.object.isRequired
};

const ZoomPopover = ({ layer, layersZoom, changeZoom, ...props }) => {
  let maxZoom = layerOps.getMaxZoom(layer);
  let minZoom = layerOps.getMinZoom(layer);
  const layerZoom = find(layersZoom, ['id', layerOps.getId(layer)]);

  if (!isNil(layerZoom)) ({ maxZoom, minZoom } = layerZoom);

  return (
    <Popover {...props} id="popover-basic">
      <Form.Group className="my-2">
        <Form.Label as={Col} className="mx-0">
          Zoom Levels
        </Form.Label>
        <DoubleRangeSlider
          domain={[0, 24]}
          maxValue={maxZoom}
          minValue={minZoom}
          changeMaxValue={value => changeZoom(value, layer)}
          changeMinValue={value => changeZoom(value, layer, false)}
        />
      </Form.Group>
    </Popover>
  );
};

ZoomPopover.propTypes = {
  changeZoom: PropTypes.func.isRequired,
  layer: PropTypes.object.isRequired,
  layersZoom: PropTypes.array.isRequired
};

const LayersSelectedList = ({
  changeTransparency,
  changeZoom,
  emptyText,
  layers,
  layersTransparency,
  layersZoom,
  listTitle,
  selectedLayers,
  selectLayer,
  styles
}) => (
  <Col xs={12} sm={6}>
    <Card>
      <Card.Header>{listTitle}</Card.Header>
      <ListGroup variant="flush">
        {isEmpty(selectedLayers) && <ListGroup.Item variant="info">{emptyText}</ListGroup.Item>}
        {selectedLayers.map(layerId => {
          const layer = find(layers, ['_id', layerId]);
          const style = find(styles, ['_id', layerOps.getStyleId(layer)]) || getDefaultStyle(layer);
          return (
            <ListGroup.Item key={layerId}>
              {layerOps.getName(layer)}
              <span
                className="close"
                style={{ cursor: 'pointer' }}
                aria-label="Close"
                aria-hidden="true"
                onClick={() => selectLayer(layerId)}
              >
                &times;
              </span>
              <OverlayTrigger
                trigger="click"
                placement="top"
                overlay={TransparencyPopover({
                  layer,
                  style,
                  layersTransparency,
                  changeTransparency
                })}
              >
                <FontAwesomeIcon
                  icon="edit"
                  size="sm"
                  className="float-right mt-1 mr-1"
                  style={{ cursor: 'pointer' }}
                />
              </OverlayTrigger>

              <OverlayTrigger
                trigger="click"
                placement="top"
                overlay={ZoomPopover({
                  layer,
                  layersZoom,
                  changeZoom
                })}
              >
                <FontAwesomeIcon
                  icon="search"
                  size="sm"
                  className="float-right mt-1 mr-1"
                  style={{ cursor: 'pointer' }}
                />
              </OverlayTrigger>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    </Card>
  </Col>
);
LayersSelectedList.propTypes = {
  changeTransparency: PropTypes.func.isRequired,
  changeZoom: PropTypes.func.isRequired,
  emptyText: PropTypes.string.isRequired,
  layers: PropTypes.array.isRequired,
  layersTransparency: PropTypes.array.isRequired,
  layersZoom: PropTypes.array.isRequired,
  listTitle: PropTypes.string.isRequired,
  selectedLayers: PropTypes.array.isRequired,
  selectLayer: PropTypes.func.isRequired,
  styles: PropTypes.array.isRequired
};

export default LayersSelectedList;
